import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  Invoice,
  RecordInvoicePaymentRequest,
  RecordInvoicePaymentResponse,
  InvoicePaymentImageUploadResponse,
} from '@stfors/shared-types';
import { api, uploadFile } from '../client';

// The invoice detail query key is ['invoices', { id }] and the lists are
// ['invoices', ...]; invalidating ['invoices'] refreshes both. Payments may draw
// from a retainer, so refresh retainers too.
function useInvalidate() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['invoices'] });
    void qc.invalidateQueries({ queryKey: ['retainers'] });
  };
}

export interface RecordPaymentVars extends RecordInvoicePaymentRequest {
  invoice_id: number;
}

export function useRecordInvoicePayment() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ invoice_id, ...body }: RecordPaymentVars) =>
      api.post<RecordInvoicePaymentResponse>(`/api/invoices/${invoice_id}/payments`, body),
    onSuccess: invalidate,
  });
}

export function useVoidInvoicePayment() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ invoice_id, payment_id }: { invoice_id: number; payment_id: number }) =>
      api.delete<{ invoice: Invoice }>(`/api/invoices/${invoice_id}/payments/${payment_id}`),
    onSuccess: invalidate,
  });
}

export function useUploadPaymentImage() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({
      invoice_id, payment_id, file, caption,
    }: { invoice_id: number; payment_id: number; file: File; caption?: string }) => {
      const qs = caption ? `?caption=${encodeURIComponent(caption)}` : '';
      return uploadFile<InvoicePaymentImageUploadResponse>(
        `/api/invoices/${invoice_id}/payments/${payment_id}/images${qs}`,
        file,
      );
    },
    onSuccess: invalidate,
  });
}

export function useDeletePaymentImage() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({
      invoice_id, payment_id, image_id,
    }: { invoice_id: number; payment_id: number; image_id: number }) =>
      api.delete<void>(`/api/invoices/${invoice_id}/payments/${payment_id}/images/${image_id}`),
    onSuccess: invalidate,
  });
}
